import { emptyScript } from "./ast.js";
class Parser {
    tokens;
    i = 0;
    partial = false;
    constructor(tokens) {
        this.tokens = tokens;
    }
    peek() {
        return this.tokens[this.i];
    }
    next() {
        return this.tokens[this.i++];
    }
    expectIdent(name) {
        const t = this.next();
        if (!t || t.kind !== "ident" || (name && t.value.toLowerCase() !== name.toLowerCase())) {
            throw new Error(`Expected identifier${name ? ` ${name}` : ""}`);
        }
        return t.value;
    }
    nextIdent() {
        return this.expectIdent();
    }
    matchIdent(name) {
        const t = this.peek();
        return !!t && t.kind === "ident" && t.value.toLowerCase() === name.toLowerCase();
    }
    matchSymbol(sym) {
        const t = this.peek();
        return !!t && t.kind === "symbol" && t.value === sym;
    }
    eatSymbol(sym) {
        if (!this.matchSymbol(sym))
            throw new Error(`Expected ${sym}`);
        this.next();
    }
    eatTag(name) {
        const t = this.peek();
        if (!t || t.kind !== "tag")
            return null;
        if (name && t.value.toLowerCase() !== name.toLowerCase())
            return null;
        this.next();
        return t.value.toLowerCase();
    }
    parseStringList() {
        if (this.matchSymbol("[")) {
            this.next();
            const values = [];
            while (!this.matchSymbol("]")) {
                const s = this.next();
                if (!s || s.kind !== "string")
                    throw new Error("Expected string in list");
                values.push(s.value);
                if (this.matchSymbol(","))
                    this.next();
            }
            this.eatSymbol("]");
            return values;
        }
        const s = this.next();
        if (!s || s.kind !== "string")
            throw new Error("Expected string");
        return [s.value];
    }
    parseString() {
        const s = this.next();
        if (!s || s.kind !== "string")
            throw new Error("Expected string");
        return s.value;
    }
    parseComparator() {
        const match = {};
        for (;;) {
            const tag = this.eatTag();
            if (!tag)
                break;
            if (tag === "localpart" || tag === "domain" || tag === "all") {
                match.addressPart = tag;
            }
            else if (tag === "comparator") {
                match.comparator = this.parseString();
            }
            else if (tag === "is" ||
                tag === "contains" ||
                tag === "matches" ||
                tag === "regex" ||
                tag === "value" ||
                tag === "count") {
                match.matchType = tag;
                if (tag === "value" || tag === "count") {
                    // relational string follows, e.g. "ge"
                    const peek = this.peek();
                    if (peek?.kind === "string") {
                        match.relational = this.parseString();
                    }
                }
            }
            else {
                // put tag back conceptually — unsupported tagged arg; mark opaque later
                this.partial = true;
                break;
            }
        }
        if (!match.matchType)
            match.matchType = "is";
        return match;
    }
    parseTest() {
        if (this.eatTag("not") || this.matchIdent("not")) {
            if (this.matchIdent("not"))
                this.next();
            return { type: "not", test: this.parseTest() };
        }
        if (this.matchIdent("true")) {
            this.next();
            return { type: "true" };
        }
        if (this.matchIdent("false")) {
            this.next();
            return { type: "false" };
        }
        if (this.matchIdent("allof") || this.matchIdent("anyof")) {
            const kind = this.nextIdent().toLowerCase();
            this.eatSymbol("(");
            const tests = [];
            while (!this.matchSymbol(")")) {
                tests.push(this.parseTest());
                if (this.matchSymbol(","))
                    this.next();
            }
            this.eatSymbol(")");
            return { type: kind, tests };
        }
        if (this.matchIdent("header")) {
            this.next();
            const match = this.parseComparator();
            const headers = this.parseStringList();
            const keys = this.parseStringList();
            return { type: "header", headers, keys, match };
        }
        if (this.matchIdent("address")) {
            this.next();
            const match = this.parseComparator();
            const headers = this.parseStringList();
            const keys = this.parseStringList();
            return { type: "address", headers, keys, match };
        }
        if (this.matchIdent("envelope")) {
            this.next();
            const match = this.parseComparator();
            const fields = this.parseStringList();
            const keys = this.parseStringList();
            return { type: "envelope", fields, keys, match };
        }
        if (this.matchIdent("size")) {
            this.next();
            if (this.eatTag("over")) {
                const n = this.next();
                if (!n || n.kind !== "number")
                    throw new Error("Expected number");
                return { type: "size", over: n.value };
            }
            if (this.eatTag("under")) {
                const n = this.next();
                if (!n || n.kind !== "number")
                    throw new Error("Expected number");
                return { type: "size", under: n.value };
            }
            throw new Error("Expected :over or :under");
        }
        if (this.matchIdent("exists")) {
            this.next();
            return { type: "exists", headers: this.parseStringList() };
        }
        if (this.matchIdent("body")) {
            this.next();
            let bodyTransform;
            if (this.eatTag("raw"))
                bodyTransform = "raw";
            else if (this.eatTag("text"))
                bodyTransform = "text";
            else if (this.eatTag("content"))
                bodyTransform = "content";
            const match = this.parseComparator();
            const keys = this.parseStringList();
            return { type: "body", keys, match, bodyTransform };
        }
        if (this.matchIdent("date")) {
            this.next();
            let zone;
            let originalzone = false;
            if (this.eatTag("originalzone"))
                originalzone = true;
            else if (this.eatTag("zone"))
                zone = this.parseString();
            const match = this.parseComparator();
            const header = this.parseString();
            const datePart = this.parseString();
            const keys = this.parseStringList();
            return { type: "date", header, datePart, keys, match, zone, originalzone };
        }
        if (this.matchIdent("mailboxexists")) {
            this.next();
            return { type: "mailboxexists", mailboxes: this.parseStringList() };
        }
        if (this.matchIdent("duplicate")) {
            this.next();
            const node = { type: "duplicate" };
            for (;;) {
                if (this.eatTag("handle"))
                    node.handle = this.parseString();
                else if (this.eatTag("header"))
                    node.header = this.parseString();
                else if (this.eatTag("uniqueid"))
                    node.uniqueid = this.parseString();
                else if (this.eatTag("seconds")) {
                    const n = this.next();
                    if (!n || n.kind !== "number")
                        throw new Error("Expected number");
                    node.seconds = n.value;
                }
                else
                    break;
            }
            return node;
        }
        if (this.matchIdent("spamtest") || this.matchIdent("virustest")) {
            const type = this.nextIdent().toLowerCase();
            const match = this.parseComparator();
            return { type, keys: this.parseStringList(), match };
        }
        // Opaque test fallback: consume until comma/paren at depth 0 — mark partial
        this.partial = true;
        const start = this.i;
        let depth = 0;
        while (this.i < this.tokens.length) {
            const t = this.peek();
            if (!t)
                break;
            if (t.kind === "symbol" && t.value === "(")
                depth += 1;
            if (t.kind === "symbol" && t.value === ")") {
                if (depth === 0)
                    break;
                depth -= 1;
            }
            if (t.kind === "symbol" && t.value === "," && depth === 0)
                break;
            if (t.kind === "symbol" && t.value === "{" && depth === 0)
                break;
            this.next();
        }
        const text = detokenize(this.tokens.slice(start, this.i));
        return { type: "opaque", text };
    }
    parseAction() {
        if (this.matchIdent("keep")) {
            this.next();
            this.eatSymbol(";");
            return { type: "keep" };
        }
        if (this.matchIdent("discard")) {
            this.next();
            this.eatSymbol(";");
            return { type: "discard" };
        }
        if (this.matchIdent("stop")) {
            this.next();
            this.eatSymbol(";");
            return { type: "stop" };
        }
        if (this.matchIdent("redirect")) {
            this.next();
            const copy = !!this.eatTag("copy");
            const address = this.parseString();
            this.eatSymbol(";");
            return { type: "redirect", address, copy };
        }
        if (this.matchIdent("reject")) {
            this.next();
            const reason = this.parseString();
            this.eatSymbol(";");
            return { type: "reject", reason };
        }
        if (this.matchIdent("fileinto")) {
            this.next();
            const copy = !!this.eatTag("copy");
            const create = !!this.eatTag("create");
            // tags may appear in either order
            const copy2 = copy || !!this.eatTag("copy");
            const create2 = create || !!this.eatTag("create");
            const mailbox = this.parseString();
            this.eatSymbol(";");
            return { type: "fileinto", mailbox, copy: copy2, create: create2 };
        }
        if (this.matchIdent("vacation")) {
            this.next();
            const node = { type: "vacation", message: "" };
            for (;;) {
                if (this.eatTag("days")) {
                    const n = this.next();
                    if (!n || n.kind !== "number")
                        throw new Error("Expected number");
                    node.days = n.value;
                }
                else if (this.eatTag("seconds")) {
                    const n = this.next();
                    if (!n || n.kind !== "number")
                        throw new Error("Expected number");
                    node.seconds = n.value;
                }
                else if (this.eatTag("subject")) {
                    node.subject = this.parseString();
                }
                else if (this.eatTag("addresses")) {
                    node.addresses = this.parseStringList();
                }
                else
                    break;
            }
            // message: string or text: block already tokenized as string
            node.message = this.parseString();
            this.eatSymbol(";");
            return node;
        }
        if (this.matchIdent("addflag") || this.matchIdent("setflag") || this.matchIdent("removeflag")) {
            const type = this.nextIdent().toLowerCase();
            const flags = this.parseStringList();
            this.eatSymbol(";");
            return { type, flags };
        }
        if (this.matchIdent("set")) {
            this.next();
            const modifiers = [];
            for (;;) {
                const tag = this.eatTag();
                if (!tag)
                    break;
                modifiers.push(tag);
            }
            const name = this.parseString();
            const value = this.parseString();
            this.eatSymbol(";");
            return { type: "set", name, value, modifiers };
        }
        if (this.matchIdent("include")) {
            this.next();
            let location = "personal";
            let optional = false;
            let once = false;
            for (;;) {
                if (this.eatTag("personal"))
                    location = "personal";
                else if (this.eatTag("global"))
                    location = "global";
                else if (this.eatTag("optional"))
                    optional = true;
                else if (this.eatTag("once"))
                    once = true;
                else
                    break;
            }
            const script = this.parseString();
            this.eatSymbol(";");
            return { type: "include", location, script, optional, once };
        }
        if (this.matchIdent("notify")) {
            this.next();
            const node = { type: "notify", method: "" };
            for (;;) {
                if (this.eatTag("from"))
                    node.from = this.parseString();
                else if (this.eatTag("importance"))
                    node.importance = this.parseString();
                else if (this.eatTag("options"))
                    node.options = this.parseStringList();
                else if (this.eatTag("message"))
                    node.message = this.parseString();
                else
                    break;
            }
            node.method = this.parseString();
            this.eatSymbol(";");
            return node;
        }
        this.partial = true;
        const start = this.i;
        while (this.i < this.tokens.length) {
            const t = this.next();
            if (!t)
                break;
            if (t.kind === "symbol" && t.value === ";")
                break;
        }
        return { type: "opaque", text: detokenize(this.tokens.slice(start, this.i)) };
    }
    parseBlock() {
        this.eatSymbol("{");
        const stmts = [];
        while (!this.matchSymbol("}")) {
            if (!this.peek())
                throw new Error("Unclosed block");
            stmts.push(this.parseStatement());
        }
        this.eatSymbol("}");
        return stmts;
    }
    parseStatement() {
        const peek = this.peek();
        if (peek?.kind === "comment") {
            this.next();
            return { type: "comment", text: peek.value };
        }
        if (this.matchIdent("require")) {
            this.next();
            const extensions = this.parseStringList();
            this.eatSymbol(";");
            return { type: "require", extensions };
        }
        if (this.matchIdent("if")) {
            this.next();
            const test = this.parseTest();
            const then = this.parseBlock();
            const elsif = [];
            while (this.matchIdent("elsif")) {
                this.next();
                const t = this.parseTest();
                const b = this.parseBlock();
                elsif.push({ test: t, then: b });
            }
            let elseBranch;
            if (this.matchIdent("else")) {
                this.next();
                elseBranch = this.parseBlock();
            }
            return { type: "if", test, then, elsif, else: elseBranch };
        }
        return { type: "action", action: this.parseAction() };
    }
    parseScript() {
        const statements = [];
        while (this.peek()) {
            try {
                statements.push(this.parseStatement());
            }
            catch {
                this.partial = true;
                const start = this.i;
                // skip to next semicolon or block end at depth 0
                let depth = 0;
                while (this.i < this.tokens.length) {
                    const t = this.next();
                    if (!t)
                        break;
                    if (t.kind === "symbol" && t.value === "{")
                        depth += 1;
                    if (t.kind === "symbol" && t.value === "}") {
                        if (depth === 0)
                            break;
                        depth -= 1;
                    }
                    if (t.kind === "symbol" && t.value === ";" && depth === 0)
                        break;
                }
                statements.push({
                    type: "opaque",
                    text: detokenize(this.tokens.slice(start, this.i)),
                });
            }
        }
        return { statements, partial: this.partial };
    }
}
function detokenize(tokens) {
    return tokens
        .map((t) => {
        switch (t.kind) {
            case "string":
                return `"${t.value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
            case "tag":
                return `:${t.value}`;
            case "number":
                return t.raw;
            case "comment":
                return `# ${t.value}`;
            default:
                return t.value;
        }
    })
        .join(" ");
}
export function tokenize(source) {
    const tokens = [];
    let i = 0;
    const s = source;
    while (i < s.length) {
        const ch = s[i];
        if (/\s/.test(ch)) {
            i += 1;
            continue;
        }
        if (ch === "#") {
            const start = i + 1;
            while (i < s.length && s[i] !== "\n")
                i += 1;
            tokens.push({ kind: "comment", value: s.slice(start, i).trim() });
            continue;
        }
        if (ch === "/" && s[i + 1] === "*") {
            i += 2;
            while (i < s.length && !(s[i] === "*" && s[i + 1] === "/"))
                i += 1;
            i += 2;
            continue;
        }
        if (ch === '"') {
            i += 1;
            let value = "";
            while (i < s.length && s[i] !== '"') {
                if (s[i] === "\\" && i + 1 < s.length) {
                    value += s[i + 1];
                    i += 2;
                }
                else {
                    value += s[i];
                    i += 1;
                }
            }
            i += 1;
            tokens.push({ kind: "string", value });
            continue;
        }
        if (ch === "t" && s.slice(i, i + 5) === "text:") {
            i += 5;
            while (i < s.length && (s[i] === " " || s[i] === "\t"))
                i += 1;
            if (s[i] === "\r")
                i += 1;
            if (s[i] === "\n")
                i += 1;
            const start = i;
            while (i < s.length) {
                if (s[i] === "\n" && s[i + 1] === ".") {
                    const after = s[i + 2];
                    if (after === undefined || after === "\n" || after === "\r" || /\s|;/.test(after)) {
                        break;
                    }
                }
                i += 1;
            }
            const value = s.slice(start, i);
            i += 2; // \n.
            tokens.push({ kind: "string", value });
            continue;
        }
        if (ch === ":") {
            i += 1;
            let value = "";
            while (i < s.length && /[A-Za-z0-9_-]/.test(s[i])) {
                value += s[i];
                i += 1;
            }
            tokens.push({ kind: "tag", value });
            continue;
        }
        if (/[{}()\[\],;]/.test(ch)) {
            tokens.push({ kind: "symbol", value: ch });
            i += 1;
            continue;
        }
        if (/[0-9]/.test(ch)) {
            let raw = "";
            while (i < s.length && /[0-9]/.test(s[i])) {
                raw += s[i];
                i += 1;
            }
            // optional size suffix K/M/G
            if (i < s.length && /[KkMmGg]/.test(s[i])) {
                raw += s[i];
                i += 1;
            }
            let value = parseInt(raw, 10);
            if (/[Kk]$/.test(raw))
                value *= 1024;
            if (/[Mm]$/.test(raw))
                value *= 1024 * 1024;
            if (/[Gg]$/.test(raw))
                value *= 1024 * 1024 * 1024;
            tokens.push({ kind: "number", value, raw });
            continue;
        }
        if (/[A-Za-z_]/.test(ch)) {
            let value = "";
            while (i < s.length && /[A-Za-z0-9_]/.test(s[i])) {
                value += s[i];
                i += 1;
            }
            tokens.push({ kind: "ident", value });
            continue;
        }
        // skip unknown char
        i += 1;
    }
    return tokens;
}
export function parseScript(source) {
    const trimmed = source.trim();
    if (!trimmed)
        return emptyScript();
    try {
        const tokens = tokenize(source);
        const parser = new Parser(tokens);
        return parser.parseScript();
    }
    catch {
        return {
            statements: [{ type: "opaque", text: source }],
            partial: true,
        };
    }
}
